import { useEffect, useRef, useState } from "react";
import {
  clearAllPicks,
  clearStoredSessionToken,
  createSession,
  deleteUser,
  fetchBootstrap,
  getStoredSessionToken,
  lookupDisplayName,
  refreshDraftKings,
  savePicks,
  setPoolLock,
  setStoredSessionToken,
  setUserPaid,
} from "./api.js";

const FALLBACK_RULES = [
  { tier: "seed_1_2", label: "1-2 seeds", limit: 3 },
  { tier: "seed_3_16", label: "3-16 seeds", limit: 5 },
];

const INSTALL_DISMISS_KEY = "march-madness-pool:install-dismissed-at";
const INSTALL_DISMISS_MS = 24 * 60 * 60 * 1000;
const AUTO_SAVE_DELAY_MS = 350;

function sortedSignature(values) {
  return [...values].sort().join("|");
}

function normalizeRules(rules) {
  return Array.isArray(rules) && rules.length ? rules : FALLBACK_RULES;
}

function teamSort(left, right) {
  const order = Object.fromEntries(FALLBACK_RULES.map((rule, index) => [rule.tier, index + 1]));
  const tierDelta = (order[left?.tier] || 99) - (order[right?.tier] || 99);
  if (tierDelta !== 0) return tierDelta;
  if ((left?.seed || 0) !== (right?.seed || 0)) return (left?.seed || 0) - (right?.seed || 0);

  const leftOdds = Number.parseInt(String(left?.draftKings?.american || "").replace(/[^\d-]/g, ""), 10);
  const rightOdds = Number.parseInt(String(right?.draftKings?.american || "").replace(/[^\d-]/g, ""), 10);
  if (Number.isFinite(leftOdds) && Number.isFinite(rightOdds) && leftOdds !== rightOdds) {
    return leftOdds - rightOdds;
  }
  return String(left?.displayName || "").localeCompare(String(right?.displayName || ""));
}

function buildTeamMap(teams) {
  const map = new Map();
  for (const team of teams) {
    map.set(team.id, team);
  }
  return map;
}

function buildTeamsByTier(teams, rules) {
  const groups = {};
  for (const rule of rules) groups[rule.tier] = [];
  for (const team of teams) {
    if (!groups[team.tier]) groups[team.tier] = [];
    groups[team.tier].push(team);
  }
  for (const key of Object.keys(groups)) {
    groups[key].sort(teamSort);
  }
  return groups;
}

function buildCounts(selectedIds, teamMap, rules) {
  const counts = { total: 0 };
  for (const rule of rules) counts[rule.tier] = 0;
  for (const id of selectedIds) {
    const team = teamMap.get(id);
    if (!team) continue;
    counts.total += 1;
    counts[team.tier] = (counts[team.tier] || 0) + 1;
  }
  return counts;
}

function buildDraftTeams(draftIds, teamMap) {
  return draftIds
    .map((id) => teamMap.get(id))
    .filter(Boolean)
    .sort(teamSort);
}

function isPhoneDevice() {
  if (typeof navigator === "undefined") return false;
  const userAgent = String(navigator.userAgent || "");
  return /iPhone|iPod|Android.+Mobile/i.test(userAgent);
}

function isCompactViewport() {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(max-width: 740px)").matches;
}

function sanitizePinInput(value) {
  return String(value || "").replace(/\D/g, "").slice(0, 4);
}

function authActionLabelForState(state) {
  switch (state) {
    case "existing":
      return "Login";
    case "new":
      return "Create account";
    case "checking":
      return "Checking...";
    default:
      return "Create or Login";
  }
}

function pickChipStateClass(pick) {
  const status = String(pick?.status || "").trim().toLowerCase();
  if (pick?.madeEliteEight || status === "elite-eight") return "is-hit";
  if (status === "playing-now") return "is-live";
  if (status === "eliminated") return "is-eliminated";
  return "is-alive";
}

function isIosSafari() {
  if (typeof navigator === "undefined") return false;
  const userAgent = String(navigator.userAgent || "");
  return /iPhone|iPod/i.test(userAgent) && /Safari/i.test(userAgent) && !/CriOS|FxiOS|EdgiOS/i.test(userAgent);
}

function isStandaloneMode() {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
}

function wasInstallPromptDismissed() {
  if (typeof window === "undefined") return false;
  try {
    const dismissedAt = Number(window.localStorage.getItem(INSTALL_DISMISS_KEY) || "");
    return Number.isFinite(dismissedAt) && Date.now() - dismissedAt < INSTALL_DISMISS_MS;
  } catch {
    return false;
  }
}

function rememberInstallPromptDismissal() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(INSTALL_DISMISS_KEY, String(Date.now()));
  } catch {}
}

function clearInstallPromptDismissal() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(INSTALL_DISMISS_KEY);
  } catch {}
}

function pickCountLabel(limit) {
  return `Pick ${limit}`;
}

function tierPanelTitle(label, limit) {
  return `Pick ${limit} Team${limit === 1 ? "" : "s"} (${label})`;
}

function Panel({ title, children, accent = "blue", className = "" }) {
  return (
    <section className={`panel panel-${accent}${className ? ` ${className}` : ""}`}>
      <div className="panelTitle">{title}</div>
      <div className="panelBody">{children}</div>
    </section>
  );
}

function Banner({ children, tone = "info" }) {
  return <div className={`banner banner-${tone}`}>{children}</div>;
}

function InstallBanner({ iosInstructions, installReady, onInstall, onDismiss }) {
  return (
    <section className="installBanner">
      <div className="installActions">
        {installReady ? (
          <button className="primaryButton" type="button" onClick={onInstall}>
            Install app
          </button>
        ) : null}
        <button className="secondaryButton" type="button" onClick={onDismiss}>
          Not now
        </button>
      </div>
    </section>
  );
}

function PostInstallBanner({ onDismiss }) {
  return (
    <section className="postInstallBanner">
      <div className="postInstallCopy">
        <div className="postInstallTitle">App installed</div>
        <div className="postInstallHint">
          Open March Madness Elite 8 from your home screen or app drawer. If the icon does not appear
          right away, wait a few seconds.
        </div>
      </div>
      <button className="secondaryButton postInstallButton" type="button" onClick={onDismiss}>
        Got it
      </button>
    </section>
  );
}

function PaymentStatus({ isPaid }) {
  return (
    <span className={`paymentStatus ${isPaid ? "is-paid" : "is-unpaid"}`}>
      <span className="paymentStatusIcon" aria-hidden="true">
        {isPaid ? "+" : "x"}
      </span>
      <span>{isPaid ? "Paid" : "Not paid"}</span>
    </span>
  );
}

function TierPanel({
  title,
  tone,
  teams,
  selectedIds,
  onToggle,
  canEdit,
  selectedCount,
  limit,
  counts,
  rules,
  footer,
}) {
  const groupComplete = selectedCount >= limit;

  return (
    <Panel title={title} accent={tone}>
      <div className="tierHeader">
        <div className="tierIntro">
          <div className={`tierMeter ${groupComplete ? "is-complete" : ""}`}>
            {selectedCount}/{limit} locked in
            {groupComplete ? (
              <span className="tierCompleteCheck" aria-hidden="true">
                &#10003;
              </span>
            ) : null}
          </div>
        </div>
      </div>
      <div className="teamGrid">
        {teams.map((team) => {
          const selected = selectedIds.includes(team.id);
          const rule = rules.find((item) => item.tier === team.tier);
          const maxedOut = !selected && (counts[team.tier] || 0) >= (rule?.limit || 0);
          const regionLabel = String(team?.regionLabel || "").trim();
          const draftKingsAmerican = String(team?.draftKings?.american || "").trim();

          return (
            <button
              key={team.id}
              type="button"
              className={`teamCard ${selected ? "is-selected" : ""} ${maxedOut ? "is-maxed" : ""}`}
              onClick={() => onToggle(team)}
              disabled={!canEdit}
              aria-pressed={selected}
              title={maxedOut ? "Click to replace your most recent pick in this group." : undefined}
            >
              <div className="teamTop">
                <div className="teamIdentity">
                  <div className="teamSeed">{team.seed}</div>
                  <div className="teamName">{team.displayName}</div>
                </div>
                {draftKingsAmerican ? <div className="teamOdds">{draftKingsAmerican}</div> : null}
              </div>
              {regionLabel ? <div className="teamMeta">{regionLabel}</div> : null}
            </button>
          );
        })}
      </div>
      {footer ? <div className="tierFooter">{footer}</div> : null}
    </Panel>
  );
}

function App() {
  const [bootstrap, setBootstrap] = useState(null);
  const [sessionToken, setSessionToken] = useState(() => getStoredSessionToken());
  const [draftIds, setDraftIds] = useState([]);
  const [displayName, setDisplayName] = useState("");
  const [displayNameMode, setDisplayNameMode] = useState("idle");
  const [loginPin, setLoginPin] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [authBusy, setAuthBusy] = useState(false);
  const [saveState, setSaveState] = useState("idle");
  const [dkRefreshBusy, setDkRefreshBusy] = useState(false);
  const [lockBusy, setLockBusy] = useState(false);
  const [clearAllBusy, setClearAllBusy] = useState(false);
  const [adminBusyKey, setAdminBusyKey] = useState("");
  const [installPrompt, setInstallPrompt] = useState({
    visible: false,
    iosInstructions: false,
    deferredPrompt: null,
  });
  const [postInstallPromptVisible, setPostInstallPromptVisible] = useState(false);
  const [compactViewport, setCompactViewport] = useState(() => isCompactViewport());
  const [picksConfirmed, setPicksConfirmed] = useState(false);
  const saveRequestRef = useRef(0);
  const autoSaveTimeoutRef = useRef(0);
  const displayNameLookupRef = useRef(0);

  async function loadData(nextToken = sessionToken, options = {}) {
    const quiet = !!options.quiet;
    if (!quiet) setLoading(true);
    setError("");

    try {
      const data = await fetchBootstrap(nextToken);
      if (data?.authInvalid && nextToken) {
        clearStoredSessionToken();
        setSessionToken("");
      }
      setBootstrap(data);
    } catch (requestError) {
      setError(String(requestError?.message || "Unable to load the pool."));
    } finally {
      if (!quiet) setLoading(false);
    }
  }

  useEffect(() => {
    loadData(sessionToken);
  }, [sessionToken]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const mediaQuery = window.matchMedia("(max-width: 740px)");
    const syncViewport = (event) => {
      setCompactViewport(event.matches);
    };

    setCompactViewport(mediaQuery.matches);
    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", syncViewport);
      return () => {
        mediaQuery.removeEventListener("change", syncViewport);
      };
    }

    mediaQuery.addListener(syncViewport);
    return () => {
      mediaQuery.removeListener(syncViewport);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (!isPhoneDevice() || isStandaloneMode()) {
      return undefined;
    }

    let postInstallTimeoutId = 0;

    if (!wasInstallPromptDismissed()) {
      setInstallPrompt({
        visible: true,
        iosInstructions: isIosSafari(),
        deferredPrompt: null,
      });
    }

    function handleBeforeInstallPrompt(event) {
      event.preventDefault();
      if (!isPhoneDevice() || isStandaloneMode() || wasInstallPromptDismissed()) return;
      setInstallPrompt({
        visible: true,
        iosInstructions: false,
        deferredPrompt: event,
      });
    }

    function handleAppInstalled() {
      clearInstallPromptDismissal();
      setInstallPrompt({
        visible: false,
        iosInstructions: false,
        deferredPrompt: null,
      });
      window.clearTimeout(postInstallTimeoutId);
      if (!isCompactViewport()) return;
      postInstallTimeoutId = window.setTimeout(() => {
        setPostInstallPromptVisible(true);
      }, 1200);
    }

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.clearTimeout(postInstallTimeoutId);
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const me = bootstrap?.me || null;
  const pool = bootstrap?.pool || null;
  const rules = normalizeRules(pool?.rules);
  const teams = Array.isArray(bootstrap?.teams) ? bootstrap.teams : [];
  const leaderRows = Array.isArray(bootstrap?.leaderboard) ? bootstrap.leaderboard : [];
  const teamMap = buildTeamMap(teams);
  const teamsByTier = buildTeamsByTier(teams, rules);
  const counts = buildCounts(draftIds, teamMap, rules);
  const draftTeams = buildDraftTeams(draftIds, teamMap);
  const totalPickLimit = rules.reduce((sum, rule) => sum + rule.limit, 0);
  const primaryRule = rules[0] || null;
  const secondaryRule = rules[1] || null;
  const ruleLabelByTier = Object.fromEntries(rules.map((rule) => [rule.tier, rule.label]));
  const savedSignature = sortedSignature(me?.pickTeamIds || []);
  const isAdmin = String(me?.user?.displayName || "").trim().toLowerCase() === "admin";
  const canEdit = Boolean(me?.user && pool?.published && !pool?.locked);
  const pickPhaseVisible = Boolean(bootstrap && !pool?.locked);
  const isDirty = sortedSignature(draftIds) !== savedSignature;
  const picksCompleted = totalPickLimit > 0 && counts.total === totalPickLimit;
  const picksRemaining = Math.max(totalPickLimit - counts.total, 0);
  const primaryRuleCount = primaryRule ? counts[primaryRule.tier] || 0 : 0;
  const secondaryRuleCount = secondaryRule ? counts[secondaryRule.tier] || 0 : 0;
  const tierGuideVisible = Boolean(
    primaryRule &&
      secondaryRule &&
      primaryRuleCount === primaryRule.limit &&
      (secondaryRuleCount < secondaryRule.limit || picksCompleted)
  );
  const secondaryRuleRemaining = secondaryRule ? Math.max(secondaryRule.limit - secondaryRuleCount, 0) : 0;
  const showInstallBanner = installPrompt.visible && compactViewport;
  const showPostInstallBanner = postInstallPromptVisible && compactViewport;
  const hasConfirmedCompletedCard = picksCompleted && picksConfirmed;
  const showMobileTopAction = compactViewport && pickPhaseVisible && picksCompleted && !isAdmin;
  const showMobileHeroConfirm = showMobileTopAction && !hasConfirmedCompletedCard;
  const showMobileHeroPicksStatus = showMobileTopAction && hasConfirmedCompletedCard;
  const showRailConfirmButton = !showMobileTopAction && !hasConfirmedCompletedCard;
  const confirmButtonLabel = !me?.user
    ? "Sign in to save"
    : !pool?.published
      ? "Bracket not ready"
      : saveState === "saving"
        ? "Saving..."
        : picksCompleted
          ? "Confirm picks"
          : "Save progress";
  const confirmButtonDisabled = !canEdit || saveState === "saving" || counts.total < 1;
  const authActionLabel = authBusy ? "Working..." : authActionLabelForState(displayNameMode);
  const tierGuideMessage = picksCompleted
    ? "All 8 picks are in. Your card is ready."
    : secondaryRule
      ? "Pick 5 more Teams Below"
      : "";

  function clearAutoSaveTimeout() {
    if (typeof window === "undefined" || !autoSaveTimeoutRef.current) return;
    window.clearTimeout(autoSaveTimeoutRef.current);
    autoSaveTimeoutRef.current = 0;
  }

  function syncSavedPicks(nextPickIds) {
    setBootstrap((current) =>
      current?.me?.user
        ? {
            ...current,
            me: {
              ...current.me,
              pickTeamIds: nextPickIds,
            },
          }
        : current
    );
  }

  async function submitDraft(nextDraftIds, options = {}) {
    const announce = options.announce === true;
    const requestId =
      typeof options.requestId === "number" ? options.requestId : saveRequestRef.current + 1;
    const nextSignature = sortedSignature(nextDraftIds);

    if (!canEdit || !me?.user?.id) {
      return false;
    }

    if (nextSignature === savedSignature) {
      setSaveState("idle");
      if (announce) {
        setError("");
        setNotice(nextDraftIds.length >= totalPickLimit ? "Picks Confirmed" : "Progress saved.");
      }
      return true;
    }

    saveRequestRef.current = requestId;
    setSaveState("saving");

    try {
      const data = await savePicks(nextDraftIds, sessionToken);
      if (saveRequestRef.current !== requestId) return false;

      const nextPickIds = Array.isArray(data?.pickTeamIds) ? data.pickTeamIds : nextDraftIds;
      setError("");
      syncSavedPicks(nextPickIds);
      setSaveState("saved");

      if (announce) {
        setNotice(nextPickIds.length >= totalPickLimit ? "Picks Confirmed" : "Progress saved.");
      }

      return true;
    } catch (requestError) {
      if (saveRequestRef.current !== requestId) return false;
      setSaveState("idle");
      setError(String(requestError?.message || "Unable to save picks."));
      return false;
    }
  }

  useEffect(() => {
    if (me?.user) {
      displayNameLookupRef.current += 1;
      setDisplayNameMode("idle");
      return undefined;
    }
    if (typeof window === "undefined") {
      return undefined;
    }

    const trimmedDisplayName = String(displayName || "").replace(/\s+/g, " ").trim();
    if (trimmedDisplayName.length < 2 || trimmedDisplayName.length > 24 || /[<>]/.test(trimmedDisplayName)) {
      displayNameLookupRef.current += 1;
      setDisplayNameMode("idle");
      return undefined;
    }

    const lookupId = displayNameLookupRef.current + 1;
    displayNameLookupRef.current = lookupId;
    setDisplayNameMode("checking");

    const controller = typeof AbortController === "undefined" ? null : new AbortController();
    const timeoutId = window.setTimeout(async () => {
      try {
        const data = await lookupDisplayName(trimmedDisplayName, {
          signal: controller?.signal,
        });
        if (displayNameLookupRef.current !== lookupId) return;
        setDisplayNameMode(data?.exists ? "existing" : "new");
      } catch (requestError) {
        if (controller?.signal.aborted) return;
        if (displayNameLookupRef.current !== lookupId) return;
        setDisplayNameMode("idle");
      }
    }, 220);

    return () => {
      window.clearTimeout(timeoutId);
      controller?.abort();
    };
  }, [displayName, me?.user]);

  useEffect(() => {
    saveRequestRef.current += 1;
    clearAutoSaveTimeout();
    setPicksConfirmed(Array.isArray(me?.pickTeamIds) && totalPickLimit > 0 && me.pickTeamIds.length === totalPickLimit);
    setSaveState("idle");
    setDraftIds(me?.pickTeamIds || []);
  }, [me?.user?.id, totalPickLimit]);

  useEffect(() => {
    if (!picksCompleted || isDirty) {
      setPicksConfirmed(false);
    }
  }, [isDirty, picksCompleted]);

  useEffect(() => {
    clearAutoSaveTimeout();

    if (!canEdit || !me?.user?.id) {
      setSaveState("idle");
      return undefined;
    }
    if (!isDirty) {
      setSaveState("idle");
      return undefined;
    }
    if (typeof window === "undefined") {
      return undefined;
    }

    const requestId = saveRequestRef.current + 1;
    saveRequestRef.current = requestId;
    setSaveState("pending");

    autoSaveTimeoutRef.current = window.setTimeout(() => {
      autoSaveTimeoutRef.current = 0;
      void submitDraft(draftIds, { requestId });
    }, AUTO_SAVE_DELAY_MS);

    return () => {
      clearAutoSaveTimeout();
    };
  }, [canEdit, draftIds, isDirty, me?.user?.id, sessionToken]);

  function clearMessages() {
    setError("");
    setNotice("");
  }

  function toggleTeam(team) {
    if (!canEdit) return;

    clearMessages();
    setDraftIds((current) => {
      if (current.includes(team.id)) {
        return current.filter((value) => value !== team.id);
      }

      const nextCounts = buildCounts(current, teamMap, rules);
      const rule = rules.find((item) => item.tier === team.tier);
      const tierCount = nextCounts[team.tier] || 0;

      if (rule && tierCount >= rule.limit) {
        const replacementId = [...current].reverse().find((id) => teamMap.get(id)?.tier === team.tier);
        if (!replacementId) {
          return current;
        }

        return [...current.filter((value) => value !== replacementId), team.id];
      }

      if (current.length >= totalPickLimit) {
        return current;
      }

      return [...current, team.id];
    });
  }

  async function handleConfirmPicks() {
    if (confirmButtonDisabled) return;

    clearMessages();
    clearAutoSaveTimeout();
    const confirmed = await submitDraft(draftIds, { announce: true });
    if (confirmed && picksCompleted) {
      setPicksConfirmed(true);
    }
  }

  async function handleLogin(event) {
    event.preventDefault();
    clearMessages();
    setAuthBusy(true);

    try {
      const data = await createSession(displayName, loginPin);
      const token = String(data?.token || "").trim();
      if (!token) throw new Error("Session token missing from server response.");
      setStoredSessionToken(token);
      setSessionToken(token);
      setDisplayName("");
      setDisplayNameMode("idle");
      setLoginPin("");
    } catch (requestError) {
      setError(String(requestError?.message || "Unable to create or log in."));
    } finally {
      setAuthBusy(false);
    }
  }

  async function handleRefreshDraftKings() {
    clearMessages();
    setDkRefreshBusy(true);

    try {
      const data = await refreshDraftKings(sessionToken);
      if (data?.authInvalid && sessionToken) {
        clearStoredSessionToken();
        setSessionToken("");
      }
      setBootstrap(data);
      setNotice("DraftKings odds refreshed.");
    } catch (requestError) {
      setError(String(requestError?.message || "Unable to refresh DraftKings odds."));
    } finally {
      setDkRefreshBusy(false);
    }
  }

  async function handleSetPoolLocked(locked) {
    if (!isAdmin) return;

    clearMessages();
    setLockBusy(true);

    try {
      const data = await setPoolLock(locked, sessionToken);
      if (data?.authInvalid && sessionToken) {
        clearStoredSessionToken();
        setSessionToken("");
      }
      setBootstrap(data);
      setNotice(`Picks ${locked ? "locked" : "unlocked"}.`);
    } catch (requestError) {
      setError(String(requestError?.message || `Unable to ${locked ? "lock" : "unlock"} picks.`));
    } finally {
      setLockBusy(false);
    }
  }

  async function handleClearAllPicks() {
    if (!isAdmin) return;
    if (typeof window !== "undefined") {
      const confirmed = window.confirm(
        "Clear all picks for the current season? This will remove every user's saved picks."
      );
      if (!confirmed) return;
    }

    clearMessages();
    setClearAllBusy(true);
    saveRequestRef.current += 1;

    try {
      const data = await clearAllPicks(sessionToken);
      if (data?.authInvalid && sessionToken) {
        clearStoredSessionToken();
        setSessionToken("");
      }
      setBootstrap(data);
      setDraftIds(Array.isArray(data?.me?.pickTeamIds) ? data.me.pickTeamIds : []);
      setSaveState("idle");
      setNotice("All picks cleared.");
    } catch (requestError) {
      setError(String(requestError?.message || "Unable to clear all picks."));
    } finally {
      setClearAllBusy(false);
    }
  }

  async function handleSetUserPaidStatus(entry, paid) {
    if (!isAdmin) return;

    clearMessages();
    const busyKey = `paid:${entry.userId}`;
    setAdminBusyKey(busyKey);

    try {
      const data = await setUserPaid(entry.userId, paid, sessionToken);
      if (data?.authInvalid && sessionToken) {
        clearStoredSessionToken();
        setSessionToken("");
      }
      setBootstrap(data);
      setNotice(`${entry.displayName} marked as ${paid ? "paid" : "not paid"}.`);
    } catch (requestError) {
      setError(String(requestError?.message || "Unable to update payment status."));
    } finally {
      setAdminBusyKey("");
    }
  }

  async function handleDeleteUserAccount(entry) {
    if (!isAdmin) return;
    if (typeof window !== "undefined") {
      const confirmed = window.confirm(
        `Delete ${entry.displayName}? This removes their account, picks, and active sessions.`
      );
      if (!confirmed) return;
    }

    clearMessages();
    const busyKey = `delete:${entry.userId}`;
    setAdminBusyKey(busyKey);

    try {
      const data = await deleteUser(entry.userId, sessionToken);
      if (data?.authInvalid && sessionToken) {
        clearStoredSessionToken();
        setSessionToken("");
      }
      setBootstrap(data);
      setNotice(`${entry.displayName} deleted.`);
    } catch (requestError) {
      setError(String(requestError?.message || "Unable to delete user."));
    } finally {
      setAdminBusyKey("");
    }
  }

  function handleLogout() {
    saveRequestRef.current += 1;
    clearStoredSessionToken();
    setSessionToken("");
    setDisplayName("");
    setDisplayNameMode("idle");
    setLoginPin("");
    setSaveState("idle");
    setBootstrap((current) => (current ? { ...current, me: null } : current));
  }

  async function handleInstallApp() {
    if (!installPrompt.deferredPrompt) return;

    try {
      await installPrompt.deferredPrompt.prompt();
      const choice = await installPrompt.deferredPrompt.userChoice;
      if (choice?.outcome !== "accepted") {
        rememberInstallPromptDismissal();
      }
    } catch {
      rememberInstallPromptDismissal();
    } finally {
      setInstallPrompt({
        visible: false,
        iosInstructions: false,
        deferredPrompt: null,
      });
    }
  }

  function handleDismissInstallPrompt() {
    rememberInstallPromptDismissal();
    setInstallPrompt({
      visible: false,
      iosInstructions: false,
      deferredPrompt: null,
    });
  }

  function handleDismissPostInstallPrompt() {
    setPostInstallPromptVisible(false);
  }

  return (
    <div className="app">
      <div className="backdrop" />
      <main className="shell">
        <header className="hero">
          <div className="heroCopy">
            <h1>March Madness Elite 8</h1>
            <p className="heroRound">
              {isAdmin ? "User control and pool settings" : `Pick ${totalPickLimit} Teams to Make Elite 8`}
            </p>
          </div>

          <div className="heroAccount">
            {!me?.user ? <div className="heroAccountLabel">4 digit pin</div> : null}
            {me?.user ? (
              <div className="userCard">
                <div className="userTopRow">
                  <div className="userName">{me.user.displayName}</div>
                  <button className="secondaryButton userInlineButton" type="button" onClick={handleLogout}>
                    Sign out
                  </button>
                </div>

                {isAdmin ? (
                  <div className="adminControls">
                    <div className="adminControlsLabel">Admin controls</div>
                    <div className="adminControlsButtons">
                      <button
                        className="secondaryButton"
                        type="button"
                        onClick={() => handleSetPoolLocked(true)}
                        disabled={lockBusy || pool?.locked === true}
                      >
                        {lockBusy && pool?.locked !== true ? "Working..." : "Lock picks"}
                      </button>
                      <button
                        className="secondaryButton"
                        type="button"
                        onClick={() => handleSetPoolLocked(false)}
                        disabled={lockBusy || pool?.locked === false}
                      >
                        {lockBusy && pool?.locked !== false ? "Working..." : "Unlock picks"}
                      </button>
                      <button
                        className="secondaryButton dangerButton"
                        type="button"
                        onClick={handleClearAllPicks}
                        disabled={clearAllBusy}
                      >
                        {clearAllBusy ? "Clearing..." : "Clear all picks"}
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : (
              <form className="joinForm" onSubmit={handleLogin}>
                <label className="fieldLabel" htmlFor="display-name">
                  Name
                </label>
                <input
                  id="display-name"
                  className="textInput"
                  type="text"
                  maxLength={24}
                  value={displayName}
                  onChange={(event) => setDisplayName(event.target.value)}
                  placeholder="Pick a display name"
                />

                <label className="fieldLabel" htmlFor="login-pin">
                  4 digit pin
                </label>
                <input
                  id="login-pin"
                  className="textInput"
                  type="password"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="\d{4}"
                  maxLength={4}
                  value={loginPin}
                  onChange={(event) => setLoginPin(sanitizePinInput(event.target.value))}
                  placeholder="4 digit pin"
                />

                <button
                  className="primaryButton"
                  type="submit"
                  disabled={authBusy || displayNameMode === "checking"}
                >
                  {authActionLabel}
                </button>
              </form>
            )}
          </div>
        </header>

        {showMobileHeroConfirm ? (
          <div className="heroConfirmBar">
            <button
              className={`secondaryButton confirmPicksButton mobileConfirmPicksButton ${picksCompleted ? "is-ready" : ""} ${isDirty ? "is-dirty" : ""}`}
              type="button"
              onClick={handleConfirmPicks}
              disabled={confirmButtonDisabled}
            >
              {confirmButtonLabel}
            </button>
          </div>
        ) : null}
        {showMobileHeroPicksStatus ? (
          <div className="heroConfirmBar">
            <div className="picksRemainingButton is-complete mobileHeroPicksStatus" aria-live="polite">
              <span className="picksRemainingLabel">Picks remaining</span>
              <span className="picksRemainingValue">All set!</span>
            </div>
          </div>
        ) : null}

        {error ? <Banner tone="danger">{error}</Banner> : null}
        {!error && notice ? <Banner tone="info">{notice}</Banner> : null}
        {showInstallBanner ? (
          <InstallBanner
            iosInstructions={installPrompt.iosInstructions}
            installReady={Boolean(installPrompt.deferredPrompt)}
            onInstall={handleInstallApp}
            onDismiss={handleDismissInstallPrompt}
          />
        ) : null}
        {showPostInstallBanner ? <PostInstallBanner onDismiss={handleDismissPostInstallPrompt} /> : null}

        {loading && !bootstrap ? (
          <Panel title="Loading">
            <p className="emptyState">Loading bracket data and leaderboard...</p>
          </Panel>
        ) : null}

        {!loading && !bootstrap ? (
          <Panel title="Unavailable">
            <p className="emptyState">The pool data could not be loaded.</p>
          </Panel>
        ) : null}

        {bootstrap ? (
          <section className={`mainGrid ${pool?.locked ? "is-locked" : ""} ${isAdmin ? "is-admin" : ""}`}>
            {!pool?.locked && !isAdmin ? (
              <div className="selectionColumn">
                {!pool?.published ? (
                  <Panel title="Waiting on Selection Sunday" accent="orange">
                    <p className="emptyState">
                      ESPN has the bracket shell up, but the seeded field is not published yet.
                      Use the refresh button once the bracket drops and the team board will fill in.
                    </p>
                  </Panel>
                ) : null}

                {pool?.published
                  ? rules.map((rule, index) => (
                      <TierPanel
                        key={rule.tier}
                        title={tierPanelTitle(rule.label, rule.limit)}
                        tone={index === 0 ? "orange" : index === 1 ? "blue" : "green"}
                        teams={teamsByTier[rule.tier] || []}
                        selectedIds={draftIds}
                        onToggle={toggleTeam}
                        canEdit={canEdit}
                        selectedCount={counts[rule.tier] || 0}
                        limit={rule.limit}
                        counts={counts}
                        rules={rules}
                        footer={
                          index === 0 && tierGuideVisible ? (
                            <div className={`tierGuideBanner ${picksCompleted ? "is-complete" : ""}`}>
                              <span className="tierGuideLabel">
                                {picksCompleted ? "All set" : "Next step"}
                              </span>
                              <span className="tierGuideText">{tierGuideMessage}</span>
                            </div>
                          ) : null
                        }
                      />
                    ))
                  : null}
              </div>
            ) : null}

            <aside className="rail">
              {pickPhaseVisible && !isAdmin ? (
                <Panel title="Pick rules" accent="blue" className="pickRulesPanel">
                  <div className="ruleStack">
                    {rules.map((rule) => (
                      <div key={rule.tier} className="ruleRow">
                        <div className="ruleSummary">
                          <div className="ruleLabel">{rule.label}</div>
                          <div className="ruleHint">{pickCountLabel(rule.limit)}</div>
                        </div>
                        <div className={`ruleBadge ${(counts[rule.tier] || 0) === rule.limit ? "is-met" : ""}`}>
                          {counts[rule.tier] || 0}/{rule.limit}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="pickRulesFooter">
                    {!showMobileHeroPicksStatus ? (
                      <div className={`picksRemainingButton ${picksCompleted ? "is-complete" : ""}`}>
                        <span className="picksRemainingLabel">Picks remaining</span>
                        <span className="picksRemainingValue">{picksCompleted ? "All set!" : picksRemaining}</span>
                      </div>
                    ) : null}
                    <div className="ruleActions">
                      <div className="ruleButtons">
                        {showRailConfirmButton ? (
                          <button
                            className={`secondaryButton confirmPicksButton ${picksCompleted ? "is-ready" : ""} ${isDirty ? "is-dirty" : ""}`}
                            type="button"
                            onClick={handleConfirmPicks}
                            disabled={confirmButtonDisabled}
                          >
                            {confirmButtonLabel}
                          </button>
                        ) : null}
                        {isAdmin ? (
                          <button
                            className="secondaryButton"
                            type="button"
                            onClick={handleRefreshDraftKings}
                            disabled={dkRefreshBusy || loading}
                          >
                            {dkRefreshBusy ? "Refreshing DK..." : "Refresh DK"}
                          </button>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </Panel>
              ) : null}

              {!pool?.locked && !isAdmin ? (
                <Panel title="Your picks" accent="blue">
                  {draftTeams.length ? (
                    <div className="selectionList">
                      {draftTeams.map((team) => (
                        <span key={team.id} className="pickChip is-alive selectionChip">
                          {team.seed} {team.displayName}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="emptyState">No teams selected yet.</p>
                  )}
                </Panel>
              ) : null}

              <Panel title={isAdmin ? "User control" : "Leaderboard"} accent="green">
                {leaderRows.length ? (
                  <div className="leaderboard">
                    {leaderRows.map((entry) => (
                      <div
                        key={entry.userId}
                        className={`leaderRow ${entry.userId === me?.user?.id ? "is-me" : ""} ${isAdmin ? "is-admin-view" : ""}`}
                      >
                        {!isAdmin ? <div className="leaderRank">#{entry.rank}</div> : null}
                        <div className="leaderBody">
                          <div className="leaderHead">
                            <div className="leaderIdentity">
                              <span className="leaderName">{entry.displayName}</span>
                              {isAdmin ? <PaymentStatus isPaid={entry.isPaid === true} /> : null}
                            </div>
                            {!isAdmin ? (
                              <span className="leaderScore">
                                <span className="leaderScoreLabel">Alive</span>
                                <span className="leaderScoreValue">
                                  {entry.aliveCount || 0} / {entry.pickCount || totalPickLimit}
                                </span>
                              </span>
                            ) : null}
                          </div>

                          {isAdmin ? (
                            <div className="leaderAdminActions">
                              <button
                                className="secondaryButton leaderAdminButton"
                                type="button"
                                onClick={() => handleSetUserPaidStatus(entry, !entry.isPaid)}
                                disabled={adminBusyKey !== "" && adminBusyKey !== `paid:${entry.userId}`}
                              >
                                {adminBusyKey === `paid:${entry.userId}`
                                  ? "Saving..."
                                  : entry.isPaid
                                    ? "Mark unpaid"
                                    : "Mark paid"}
                              </button>
                              {String(entry.displayName || "").trim().toLowerCase() !== "admin" ? (
                                <button
                                  className="secondaryButton dangerButton leaderAdminButton"
                                  type="button"
                                  onClick={() => handleDeleteUserAccount(entry)}
                                  disabled={adminBusyKey !== "" && adminBusyKey !== `delete:${entry.userId}`}
                                >
                                  {adminBusyKey === `delete:${entry.userId}` ? "Deleting..." : "Delete user"}
                                </button>
                              ) : null}
                            </div>
                          ) : null}

                          {!isAdmin ? (
                            <div className="leaderPicks">
                              {pool?.locked ? (
                                entry.picks.length ? (
                                  entry.picks
                                    .sort((left, right) => (left.seed || 0) - (right.seed || 0))
                                    .map((pick) => (
                                      <span
                                        key={`${entry.userId}-${pick.teamId}`}
                                        className={`pickChip ${pickChipStateClass(pick)}`}
                                      >
                                        {pick.teamName}
                                      </span>
                                    ))
                                ) : (
                                  <span className="leaderEmpty">No picks saved yet</span>
                                )
                              ) : (
                                <span className="leaderEmpty">Picks hidden until lock.</span>
                              )}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="emptyState">No one has joined yet.</p>
                )}
              </Panel>
            </aside>
          </section>
        ) : null}
      </main>
    </div>
  );
}

export default App;
